import React, { useEffect } from "react";
import { ActionsMenu } from "../common/components/ActionsMenu";
import { ConditionalWrapper } from "../common/components/ConditionalWrapper";
import { MENU_ACTIONS, MENU_FILTERS } from "../common/components/modals/constants";
import useModal from "../common/components/modals/useModal";
import { useAccessWindowSize } from "../common/layout/useAccessWindowSize";
import { USER_ROLE_NEWS } from "../common/roles/constants";
import useRolesVisibility from "../common/roles/useRolesVisibility";
import { ActionsMenuButton } from "../search/actions/ActionsMenuButton";
import { RequiredRoles } from "../search/criteria/components/common/requiredRoles";
import { SupportLink } from "../search/header/SupportLink";
import { SearchResultsPreviewContainer } from "../search/preview/SearchResultsPreviewContainer";
import { AlertLink } from "./AlertLink";
import { ItemsPerPageDropdown } from "./ItemsPerPageDropdown";
import { layoutSwitcherType, searchTitleType, supportLinkType } from "../common/types/search/search";
import { eventButtonType } from "../common/types/common";

interface NewsSearchHeaderProps {
	title?: searchTitleType;
	layoutSwitcher?: layoutSwitcherType;
	supportLink?: supportLinkType;
}

export function NewsSearchHeader({
	title,
	layoutSwitcher,
	supportLink,
}: NewsSearchHeaderProps) {
	const { isMobile } = useAccessWindowSize();
	const { openedModals, onOpenModal, clearModals } = useModal();
	const { hasRequiredRoles } = useRolesVisibility(
		RequiredRoles.showFor([USER_ROLE_NEWS]),
	);

	const actionsMenuIsOpened = openedModals.includes(MENU_ACTIONS);
	const filtersMenuIsOpened = openedModals.includes(MENU_FILTERS);

	useEffect(() => {
		if (!isMobile && (actionsMenuIsOpened || filtersMenuIsOpened)) {
			clearModals();
		}
	}, [isMobile, actionsMenuIsOpened, filtersMenuIsOpened]);

	const handlePrintPageClick = (e: eventButtonType) => {
		e.currentTarget.blur();
		window.print();
	};

	const filterNewsButton = (
		<button
			type="button"
			className="btn btn--secondary btn--has-icon btn--filters"
			onClick={() => onOpenModal(MENU_FILTERS)}
		>
			<span>Filter news</span>
		</button>
	);

	return (
		<header className="search-header">
			<div className="results-count">
				<h1 className="h4 with-square">{title}</h1>
				<SearchResultsPreviewContainer />
			</div>

			<div className="search-actions flex align-center justify-space-between">
				{layoutSwitcher}

				<ConditionalWrapper
					condition={isMobile}
					wrapper={(children) => (
						<ActionsMenu id={MENU_ACTIONS}>{children}</ActionsMenu>
					)}
				>
					<ItemsPerPageDropdown />
					<SupportLink supportLink={supportLink} />
					<AlertLink />

					<button
						type="button"
						className="btn btn--secondary btn--has-icon btn--print"
						onClick={handlePrintPageClick}
					>
						<span>Print Page</span>
					</button>
				</ConditionalWrapper>

				<ActionsMenuButton onClick={() => onOpenModal(MENU_ACTIONS)} />

				{hasRequiredRoles && filterNewsButton}
			</div>
		</header>
	);
}
